import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { Button } from '../components/ui/Button';
import {
    FileText,
    Database,
    ChevronRight,
    Plus,
    Clock,
    CheckCircle,
    Loader,
    Search,
} from 'lucide-react';
import { getReportsForDatabase } from '../services/api';
import { formatLocalDate } from '../utils/timezone';

export const ReportsLanding = () => {
    const { t } = useTranslation(['pages-misc-2', 'common']);
    const navigate = useNavigate();
    const { databases } = useDatabase();
    const { showToast } = useToast();

    const [loading, setLoading] = useState(true);
    const [reportsByDatabase, setReportsByDatabase] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    const fetchAllReports = React.useCallback(async () => {
        setLoading(true);
        const reportsMap = {};

        try {
            await Promise.all(
                databases.map(async (db) => {
                    try {
                        const response = await getReportsForDatabase(db.id);
                        const reports = response.data?.data?.reports || [];
                        reportsMap[db.id] = reports;
                    } catch (error) {
                        console.error(`Failed to fetch reports for database ${db.id}:`, error);
                        reportsMap[db.id] = [];
                    }
                })
            );
            setReportsByDatabase(reportsMap);
        } catch (error) {
            console.error('Failed to fetch all reports:', error);
            showToast(t('pages-misc-2:reportsLanding.failedToLoadReports'), 'error');
        } finally {
            setLoading(false);
        }
    }, [databases, showToast, t]);

    useEffect(() => {
        fetchAllReports();
    }, [fetchAllReports]);

    const filteredDatabases = databases.filter(db =>
        db.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getTotalReports = () => {
        return Object.values(reportsByDatabase).reduce((sum, reports) => sum + reports.length, 0);
    };

    const getCompletedReports = () => {
        return Object.values(reportsByDatabase).reduce((sum, reports) => 
            sum + reports.filter(r => r.status === 'completed').length, 0
        );
    };

    return (
        <div className="page-content">
            {/* Header */}
            <div className="mb-8">
                <h1 className="page-title mb-2">{t('pages-misc-2:reportsLanding.title')}</h1>
                <p className="page-subtitle mb-0">{t('pages-misc-2:reportsLanding.subtitle')}</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center shrink-0">
                            <FileText className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600 mb-1">{t('pages-misc-2:reportsLanding.totalReports')}</p>
                            <p className="text-3xl font-bold text-gray-900">{getTotalReports()}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center shrink-0">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600 mb-1">{t('pages-misc-2:reportsLanding.completedReports')}</p>
                            <p className="text-3xl font-bold text-green-600">{getCompletedReports()}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center shrink-0">
                            <Database className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600 mb-1">{t('pages-misc-2:reportsLanding.databasesWithReports')}</p>
                            <p className="text-3xl font-bold text-gray-900">
                                {Object.values(reportsByDatabase).filter(r => r.length > 0).length} / {databases.length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="mb-6">
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('pages-misc-2:reportsLanding.searchDatabasesPlaceholder')}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                </div>
            </div>

            {/* Databases List */}
            {loading ? (
                <div className="text-center py-12">
                    <Loader className="w-8 h-8 animate-spin mx-auto text-primary-600 mb-4" />
                    <p className="text-gray-600">{t('pages-misc-2:reportsLanding.loadingReports')}</p>
                </div>
            ) : filteredDatabases.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-xl font-semibold mb-2">{t('pages-misc-2:reportsLanding.noDatabasesFound')}</h3>
                    <p className="text-gray-600 mb-4">
                        {searchTerm ? t('pages-misc-2:reportsLanding.noDatabasesMatchSearch') : t('pages-misc-2:reportsLanding.connectDatabaseToStart')}
                    </p>
                    {!searchTerm && (
                        <Button
                            onClick={() => navigate('/database-connection')}
                            variant="primary"
                            className="flex items-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            {t('pages-misc-2:reportsLanding.connectDatabase')}
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredDatabases.map((db) => {
                        const reports = reportsByDatabase[db.id] || [];
                        const completedCount = reports.filter(r => r.status === 'completed').length;

                        return (
                            <div
                                key={db.id}
                                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => navigate(`/reports/${db.id}`)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4 flex-1">
                                        <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                                            <Database className="w-6 h-6 text-primary-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                                {db.name}
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-2">
                                                {db.db_type} • {t('pages-misc-2:reportsLanding.connected')} {formatLocalDate(db.created_at)}
                                            </p>
                                            <div className="flex items-center gap-4 text-sm">
                                                <span className="flex items-center gap-1 text-gray-600">
                                                    <FileText className="w-4 h-4" />
                                                    {t('pages-misc-2:reportsLanding.reportsCount', { count: reports.length })}
                                                </span>
                                                <span className="flex items-center gap-1 text-green-600">
                                                    <CheckCircle className="w-4 h-4" />
                                                    {t('pages-misc-2:reportsLanding.completedCount', { count: completedCount })}
                                                </span>
                                                {reports.length > 0 && (
                                                    <span className="flex items-center gap-1 text-gray-500">
                                                        <Clock className="w-4 h-4" />
                                                        {t('pages-misc-2:reportsLanding.last')}: {formatLocalDate(reports[0]?.created_at)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/reports/${db.id}`);
                                            }}
                                            variant="primary"
                                            className="flex items-center gap-2"
                                        >
                                            <Plus className="w-4 h-4" />
                                            {t('pages-misc-2:reportsLanding.newReport')}
                                        </Button>
                                        <ChevronRight className="w-5 h-5 text-gray-400" />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ReportsLanding;
